import React, { useCallback } from 'react';
import styled from 'styled-components/macro';
import { EyeIcon } from 'assets/icons';
import Tooltip from 'shared/components/Tooltip';
import { IAttachmentOption } from '../../../interfaces/IAttachmentOption';
import { ChannelMessageAttachmentType } from 'shared/types/graphql';
import { getFileExtension } from 'shared/utils';
import PreviewVideoFallbackThumbnail from 'assets/avatars/video-play-thumbnail.jpg';

const previewVideoFileTypes = ['mp4', 'ogg', 'ogv', 'webm'];

const validatePreviewVideoFileType = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  if (!ext) return false;
  return previewVideoFileTypes.includes(ext.toLowerCase());
};

interface IChatMediaPreviewProps {
  stagedFile?: File;
  attachment: IAttachmentOption;
  onAttachmentRemove: (attachment: IAttachmentOption) => void;
  renderTypeIcon: (attachmentType: ChannelMessageAttachmentType) => JSX.Element | null;
  isMedia: boolean;
  setPreviewData: (fileUrl: string, attachmentType: ChannelMessageAttachmentType, fileName: string) => void;
}

const ChatMediaPreview = ({
  stagedFile,
  attachment,
  onAttachmentRemove,
  renderTypeIcon,
  isMedia,
  setPreviewData,
}: IChatMediaPreviewProps): JSX.Element | null => {
  const fileURL = !!stagedFile?.name ? URL.createObjectURL(stagedFile) : '';
  const attachmentType = attachment.value.attachmentType;

  const handlePreviewClick = useCallback(() => {
    // Do not allow preview of non html5 compatible video
    if (
      attachmentType === ChannelMessageAttachmentType.Video &&
      !validatePreviewVideoFileType(stagedFile?.name || '')
    ) {
      return;
    }

    if (fileURL) setPreviewData(fileURL, attachmentType, stagedFile?.name || '');
  }, [fileURL, setPreviewData, attachmentType, stagedFile]);

  // TODO: Move invalid file removal (and perhaps a user prompt) to a central place
  if (!fileURL) onAttachmentRemove(attachment);

  const hasPreview =
    attachmentType === ChannelMessageAttachmentType.Video && !validatePreviewVideoFileType(stagedFile?.name || '')
      ? false
      : true;

  if (stagedFile && stagedFile.type) {
    return (
      <>
        <Tooltip disabled={!hasPreview} content={`Preview ${attachmentType}`} delay={[500, 0]}>
          {isMedia ? (
            <StyledFilePreview hasPreview={hasPreview} onClick={handlePreviewClick}>
              <PreviewOverlay hasPreview={hasPreview}>
                <StyledEyeIcon />
              </PreviewOverlay>
              {attachmentType === ChannelMessageAttachmentType.Image && (
                <PreviewImage src={fileURL} alt={stagedFile.name} />
              )}
              {attachmentType === ChannelMessageAttachmentType.Video &&
                validatePreviewVideoFileType(stagedFile?.name || '') && (
                  <PreviewVideo src={fileURL}>Your browser does not support the video tag.</PreviewVideo>
                )}
              {attachmentType === ChannelMessageAttachmentType.Video &&
                !validatePreviewVideoFileType(stagedFile?.name || '') && (
                  <PreviewImage src={PreviewVideoFallbackThumbnail} alt={stagedFile.name} />
                )}
            </StyledFilePreview>
          ) : (
            <PreviewDefaultFile onClick={handlePreviewClick}>{renderTypeIcon(attachmentType)}</PreviewDefaultFile>
          )}
        </Tooltip>
      </>
    );
  } else return null;
};

const StyledFilePreview = styled.div<{ hasPreview: boolean }>`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  height: 100%;
  width: 58px;
  border-radius: 10px;
  overflow: hidden;
  cursor: ${({ hasPreview }): string => (hasPreview ? 'pointer' : 'default')};
  position: relative;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewDefaultFile = styled.div`
  cursor: pointer;
`;

const PreviewOverlay = styled.div<{ hasPreview: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  background: rgba(0, 0, 0, 0.6);
  transition: opacity 0.1s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  ${StyledFilePreview}:hover & {
    opacity: ${({ hasPreview }): number => (hasPreview ? 1 : 0)};
  }
`;

const StyledEyeIcon = styled(EyeIcon)`
  width: 16px;
  height: 16px;

  & * {
    fill: #fff;
  }
`;

export default ChatMediaPreview;
